import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowLeft, Briefcase, CalendarDays, CheckCircle, ChevronLeft, ChevronRight, Loader2, Save } from 'lucide-react';
import {
    getWarehouseScheduleAssignments,
    getWarehouseScheduleAvailability,
    getWarehouseScheduleMe,
    saveWarehouseScheduleAvailability,
} from './api';
import { useLanguage } from './LanguageContext';

const TIME_OPTIONS = ['不可上班', '上午 09:00-13:00', '下午 13:00-17:00', '整天 09:00-17:00', '10:00-15:00'];

function toDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function dateFromKey(dateKey) {
    const [year, month, day] = String(dateKey).split('-').map(Number);
    return new Date(year, month - 1, day);
}

function addDays(dateKey, days) {
    const date = dateFromKey(dateKey);
    date.setDate(date.getDate() + days);
    return toDateKey(date);
}

function nextWeekStartFromToday(todayKey) {
    const date = todayKey ? dateFromKey(todayKey) : new Date();
    const day = date.getDay();
    const offset = (day + 6) % 7;
    date.setDate(date.getDate() - offset + 7);
    return toDateKey(date);
}

function fallbackDateLabel(dateKey) {
    const date = dateFromKey(dateKey);
    const weekday = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()];
    return `${date.getMonth() + 1}月${date.getDate()}日 ${weekday}`;
}

function normalizeDays(days, weekStart) {
    if (Array.isArray(days) && days.length) {
        return days.map((day) => ({
            dateKey: day.dateKey,
            dateLabel: day.dateLabel || fallbackDateLabel(day.dateKey),
            timeRange: day.timeRange || '不可上班',
            note: day.note || '',
        }));
    }

    return Array.from({ length: 7 }, (_, index) => {
        const dateKey = addDays(weekStart, index);
        return {
            dateKey,
            dateLabel: fallbackDateLabel(dateKey),
            timeRange: '不可上班',
            note: '',
        };
    });
}

function isCanceled(error) {
    return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
}

export default function WorkPlanScreen({ onBack }) {
    const { t } = useLanguage();
    const [weekStart, setWeekStart] = useState(() => nextWeekStartFromToday());
    const [me, setMe] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseId, setWarehouseId] = useState('');
    const [days, setDays] = useState(() => normalizeDays([], nextWeekStartFromToday()));
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');
    const loadFailedText = t('workPlan.loadFailed');

    const activeWarehouse = useMemo(
        () => warehouses.find((warehouse) => warehouse.id === warehouseId) || warehouses[0],
        [warehouseId, warehouses]
    );

    const workDayCount = useMemo(
        () => days.filter((day) => day.timeRange && day.timeRange !== '不可上班').length,
        [days]
    );

    const scheduleDays = schedule?.days || [];

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');

        getWarehouseScheduleMe({ signal: controller.signal })
            .then((meResponse) => {
                const mePayload = meResponse.data;
                const nextWarehouses = mePayload.warehouses || [];
                const nextWeekStart = nextWeekStartFromToday(mePayload.todayKey);

                setMe(mePayload);
                setWarehouses(nextWarehouses);
                setWeekStart(nextWeekStart);
                setWarehouseId(nextWarehouses[0]?.id || '');
                setProfileLoaded(true);

                if (!nextWarehouses.length) {
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (isCanceled(err)) return;
                const message = err.response?.data?.message || loadFailedText;
                setError(message);
                setLoading(false);
                setProfileLoaded(true);
            });

        return () => controller.abort();
    }, [loadFailedText]);

    useEffect(() => {
        if (!profileLoaded || !warehouseId) {
            return undefined;
        }

        const controller = new AbortController();
        setLoading(true);
        setError('');

        Promise.all([
            getWarehouseScheduleAvailability(weekStart, warehouseId, { signal: controller.signal }),
            getWarehouseScheduleAssignments(weekStart, { signal: controller.signal }),
        ])
            .then(([availabilityResponse, scheduleResponse]) => {
                const availabilityPayload = availabilityResponse.data;
                const nextWarehouses = availabilityPayload.warehouses || [];

                if (nextWarehouses.length) {
                    setWarehouses(nextWarehouses);
                    setWarehouseId((current) => {
                        if (current && nextWarehouses.some((warehouse) => warehouse.id === current)) {
                            return current;
                        }

                        return availabilityPayload.warehouseId || nextWarehouses[0]?.id || '';
                    });
                } else if (availabilityPayload.warehouseId) {
                    setWarehouseId(availabilityPayload.warehouseId);
                }

                setDays(normalizeDays(availabilityPayload.days, availabilityPayload.weekStart || weekStart));
                setSchedule(scheduleResponse.data);
            })
            .catch((err) => {
                if (isCanceled(err)) return;
                const message = err.response?.data?.message || loadFailedText;
                setError(message);
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [profileLoaded, warehouseId, weekStart, loadFailedText]);

    useEffect(() => {
        if (!toast) return undefined;
        const timer = window.setTimeout(() => setToast(''), 2200);
        return () => window.clearTimeout(timer);
    }, [toast]);

    function updateDay(dateKey, field, value) {
        setDays((current) => current.map((day) => (
            day.dateKey === dateKey ? { ...day, [field]: value } : day
        )));
    }

    async function submitAvailability() {
        if (!warehouseId) {
            setError(t('workPlan.noWarehouse'));
            return;
        }

        setSaving(true);
        setError('');

        try {
            const response = await saveWarehouseScheduleAvailability({
                weekStart,
                warehouseId,
                days: days.map((day) => ({
                    dateKey: day.dateKey,
                    timeRange: day.timeRange,
                    note: day.note || '',
                })),
            });

            const availability = response.data?.availability;
            setDays(normalizeDays(availability?.days, availability?.weekStart || weekStart));
            setToast(t('workPlan.saved'));
        } catch (err) {
            setError(err.response?.data?.message || t('workPlan.saveFailed'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="p-4 md:p-6 pb-20 md:pb-6 bg-gray-50 min-h-screen">
            <div className="flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="p-2 rounded-full bg-white shadow-sm text-gray-600 hover:text-indigo-600 transition-colors"
                    title={t('workPlan.back')}
                >
                    <ArrowLeft size={22} />
                </button>
                <div className="flex-1">
                    <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">{t('workPlan.kicker')}</p>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('workPlan.title')}</h1>
                </div>
                <div className="hidden sm:flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700">
                    <CalendarDays size={18} />
                    {workDayCount}{t('workPlan.workDaysUnit')}
                </div>
            </div>

            {error && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <section className="mt-4 rounded-xl bg-white p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                        <Briefcase size={22} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm text-gray-500">{t('workPlan.currentUser')}</p>
                        <h2 className="text-lg font-bold text-gray-800 truncate">
                            {me?.user?.name || t('workPlan.loadingUser')}
                        </h2>
                        <p className="text-sm text-gray-500 truncate">
                            {activeWarehouse?.name || t('workPlan.loadingWarehouse')}
                        </p>
                    </div>
                </div>
            </section>

            <section className="mt-4 rounded-xl bg-white p-4 shadow-sm border border-gray-100 space-y-4">
                <label className="block">
                    <span className="block text-sm font-semibold text-gray-700 mb-2">{t('workPlan.warehouse')}</span>
                    <select
                        value={warehouseId}
                        onChange={(event) => setWarehouseId(event.target.value)}
                        disabled={!warehouses.length || loading}
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
                    >
                        {!warehouses.length && <option value="">{t('workPlan.noWarehouse')}</option>}
                        {warehouses.map((warehouse) => (
                            <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                            </option>
                        ))}
                    </select>
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <button
                        type="button"
                        onClick={() => setWeekStart(addDays(weekStart, -7))}
                        className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        <ChevronLeft size={18} />
                        {t('workPlan.prevWeek')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setWeekStart(addDays(weekStart, 7))}
                        className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        {t('workPlan.nextWeek')}
                        <ChevronRight size={18} />
                    </button>
                </div>
            </section>

            <section className="mt-4 rounded-xl bg-white p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('workPlan.weekTitle')}</h2>
                        <p className="text-sm text-gray-500">{weekStart} - {addDays(weekStart, 6)}</p>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                        {workDayCount}{t('workPlan.workDaysUnit')}
                    </span>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12 text-indigo-600">
                        <Loader2 className="animate-spin" size={32} />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {days.map((day) => (
                            <article key={day.dateKey} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <strong className="block text-gray-800">{day.dateLabel}</strong>
                                        <span className="text-xs text-gray-500">{day.dateKey}</span>
                                    </div>
                                    {day.timeRange !== '不可上班' && (
                                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                                            {t('workPlan.available')}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                                    <select
                                        value={day.timeRange}
                                        onChange={(event) => updateDay(day.dateKey, 'timeRange', event.target.value)}
                                        className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    >
                                        {TIME_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        value={day.note || ''}
                                        onChange={(event) => updateDay(day.dateKey, 'note', event.target.value)}
                                        placeholder={t('workPlan.notePlaceholder')}
                                        className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    />
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                <button
                    type="button"
                    onClick={submitAvailability}
                    disabled={saving || loading || !warehouseId}
                    className="mt-4 w-full flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-md hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500"
                >
                    {saving ? (
                        <>
                            <Loader2 className="animate-spin mr-2" size={18} />
                            {t('workPlan.saving')}
                        </>
                    ) : (
                        <>
                            <Save className="mr-2" size={18} />
                            {t('workPlan.submit')}
                        </>
                    )}
                </button>
            </section>

            <section className="mt-4 rounded-xl bg-white p-4 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold text-gray-800">{t('workPlan.mySchedule')}</h2>
                <div className="mt-3 space-y-2">
                    {scheduleDays.length ? scheduleDays.map((day) => (
                        <article key={day.dateKey} className="rounded-lg border border-gray-100 p-3">
                            <strong className="block text-sm text-gray-800">{day.dateLabel}</strong>
                            <div className="mt-1 flex flex-col gap-1 text-sm text-gray-600">
                                {day.assignments?.length ? day.assignments.map((item, index) => (
                                    <span key={`${day.dateKey}-${item.warehouseId}-${index}`} className="rounded-md bg-indigo-50 px-2 py-1 text-indigo-700">
                                        {item.warehouseName} · {item.timeRange} · {item.taskType}
                                    </span>
                                )) : (
                                    <span className="text-gray-400">{t('workPlan.unassigned')}</span>
                                )}
                            </div>
                        </article>
                    )) : (
                        <p className="py-4 text-center text-sm text-gray-500">{t('workPlan.scheduleEmpty')}</p>
                    )}
                </div>
            </section>

            {toast && (
                <div className="fixed left-1/2 top-5 z-50 -translate-x-1/2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-lg flex items-center gap-2">
                    <CheckCircle size={18} />
                    {toast}
                </div>
            )}
        </div>
    );
}
